import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { Modal } from 'react-bootstrap'
import Layout from '../../layout'
import Breadcrums from '../../components/common/breadcrumbs'
import CustomPagination from '../../components/common/custompagination'
import CustomRangepicker from '../../components/common/rangepicker'
import Loader from '../../components/common/loader'
import { capitalizeAllFirstLetters, formateDate, formateDateWithTime } from '../../helpers/commonFunctions'
import { GetCompanyList } from '../../services/companyService'
import { GetBrandList } from '../../services/brandService'
import { GetOutletManagementList } from '../../services/outletManagementService'
import {
    DeleteFailedPriceValidation,
    DeleteManyFailedPriceValidations,
    GetFailedPriceValidationDetail,
    GetFailedPriceValidationList,
    GetFailedPriceValidationStats,
} from '../../services/failedPriceValidationService'

const VALIDATOR_TYPES = [
    { value: '', label: 'All validators' },
    { value: 'old_validator', label: 'Old validator' },
    { value: 'new_validator', label: 'New validator' },
];

const OPERATIONS = [
    { value: '', label: 'All operations' },
    { value: 'create', label: 'Create' },
    { value: 'recall', label: 'Recall' },
];

const FailedPriceValidationsList = () => {

    const breadcrumbs = [{ title: "Dashboard", url: "/admin/dashboard" }, { title: "Failed Price Validations", url: "" }];
    const location = useLocation();
    const params = useParams();

    const [list, setList] = useState([]);
    const [stats, setStats] = useState([]);
    const [dataLength, setDataLength] = useState(0);
    const [itemPerPage] = useState(25);
    const [page, setPage] = useState(Number(params?.id) || 1);
    const [loader, setLoader] = useState(true);
    const [statsUpdate, setStatsUpdate] = useState(false);
    const [resetdate, setResetDate] = useState(false);

    // Filters map one-to-one onto the API's own filter names.
    const [search, setSearch] = useState('');
    const [validatorType, setValidatorType] = useState('');
    const [operation, setOperation] = useState('');
    const [dateRange, setDateRange] = useState({});
    // Kept apart from created_at: an outlet trading past midnight books the order
    // under the previous business date, so the two ranges answer different questions.
    const [businessDateRange, setBusinessDateRange] = useState({});

    // Company / brand / outlet cascade. companyId is the admin companies.id used to
    // reach the tenant db; companyRegisterId is companies.company_id, which is what
    // the failed_price_validations rows actually store.
    const [companies, setCompanies] = useState([]);
    const [tenantLists, setTenantLists] = useState({});
    const tenantRequests = useRef({});
    const [companyId, setCompanyId] = useState('');
    const [companyRegisterId, setCompanyRegisterId] = useState('');
    const [brandId, setBrandId] = useState('');
    const [outletId, setOutletId] = useState('');

    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(false);

    const [detail, setDetail] = useState(null);
    const [detailLoader, setDetailLoader] = useState(false);
    const [showDetail, setShowDetail] = useState(false);

    // The filter set both the list and the stats read, so the summary always
    // describes the rows on screen.
    const buildFilters = () => {
        const filters = {};
        if (search) filters.search = search;
        if (validatorType) filters.validator_type = validatorType;
        if (operation) filters.operation = operation;
        if (dateRange?.start) filters.from = dateRange.start;
        if (dateRange?.to || dateRange?.end) filters.to = dateRange.to || dateRange.end;
        if (businessDateRange?.start) filters.business_date_from = businessDateRange.start;
        if (businessDateRange?.to || businessDateRange?.end) filters.business_date_to = businessDateRange.to || businessDateRange.end;
        if (companyRegisterId) filters.company_register_id = String(companyRegisterId);
        if (brandId) filters.brand_id = Number(brandId);
        if (outletId) filters.outlet_id = Number(outletId);
        return filters;
    };

    const activeFilterCount = Object.keys(buildFilters()).length;

    useEffect(() => {
        setSelectedIds([]);
        setLoader(true);
        GetFailedPriceValidationList({ ...buildFilters(), page, per_page: itemPerPage }).then(data => {
            const response = data?.data?.response || {};
            setDataLength(response.count || 0);
            setList(response.rows || []);
            setLoader(false);
        }).catch(error => {
            setLoader(false);
            console.log("error ====> ", error);
        })
    }, [location, page, search, validatorType, operation, dateRange, businessDateRange, companyRegisterId, brandId, outletId, statsUpdate])

    useEffect(() => {
        GetFailedPriceValidationStats(buildFilters()).then(data => {
            setStats(data?.data?.response || []);
        }).catch(error => {
            console.log("error ====> ", error);
        })
    }, [search, validatorType, operation, dateRange, businessDateRange, companyRegisterId, brandId, outletId, statsUpdate])

    // Brands and outlets live in each tenant's own database, so they are fetched per
    // company and cached here — keyed by the admin companies.id. This backs both the
    // dropdowns and the name columns, so rows show names even with no company filter.
    const listRequest = (extra = {}) => {
        const formData = new FormData();
        formData.append("page", 1);
        formData.append("per_page", 1000);
        formData.append("sort", JSON.stringify({}));
        formData.append("search", JSON.stringify({}));
        formData.append("global_search", "");
        Object.entries(extra).forEach(([key, value]) => formData.append(key, value));
        return formData;
    };

    const loadTenantLists = (adminCompanyId) => {
        if (!adminCompanyId) return;
        // Same-render duplicates would otherwise fire one request per row.
        if (tenantRequests.current[adminCompanyId]) return;
        tenantRequests.current[adminCompanyId] = true;

        Promise.all([
            GetBrandList(listRequest({ company_id: adminCompanyId })).catch(() => null),
            GetOutletManagementList(listRequest({ company_id: adminCompanyId })).catch(() => null),
        ]).then(([brandData, outletData]) => {
            setTenantLists(prev => ({
                ...prev,
                [adminCompanyId]: {
                    brands: brandData?.data?.response?.rows || [],
                    outlets: outletData?.data?.response?.rows || [],
                },
            }));
        }).catch(error => {
            console.log("error ====> ", error);
        })
    };

    useEffect(() => {
        GetCompanyList(listRequest()).then(data => {
            setCompanies(data?.data?.response?.rows || []);
        }).catch(error => {
            console.log("error ====> ", error);
        })
    }, [])

    // A company change drops the narrower picks, which would otherwise keep
    // filtering by ids belonging to the previous company.
    useEffect(() => {
        setBrandId('');
        setOutletId('');
        loadTenantLists(companyId);
    }, [companyId])

    // Whatever companies the current page happens to show, load their names once.
    useEffect(() => {
        if (!companies.length || !list.length) return;
        const registerIds = [...new Set(list.map(row => String(row?.company_register_id)).filter(Boolean))];
        registerIds.forEach(registerId => {
            const company = companies.find(item => String(item?.company_id) === registerId);
            if (company) loadTenantLists(company.id);
        });
    }, [list, companies])

    const adminCompanyIdFor = (registerId) => {
        const company = companies.find(item => String(item?.company_id) === String(registerId));
        return company?.id;
    };

    const brands = tenantLists[companyId]?.brands || [];
    const outlets = tenantLists[companyId]?.outlets || [];

    const handleCompanyChange = (value) => {
        const company = companies.find(row => String(row.id) === String(value));
        setPage(1);
        setCompanyId(value);
        setCompanyRegisterId(company?.company_id || '');
    };

    // The outlet endpoint has no brand filter, but each row carries its brand, so
    // narrowing happens here.
    const outletOptions = brandId
        ? outlets.filter(row => String(row?.brand_id) === String(brandId))
        : outlets;

    const resetFilter = (e) => {
        e.preventDefault();
        setSearch('');
        setValidatorType('');
        setOperation('');
        setDateRange({});
        setBusinessDateRange({});
        setCompanyId('');
        setCompanyRegisterId('');
        setBrandId('');
        setOutletId('');
        setResetDate(!resetdate);
    };

    const viewFunction = (id) => {
        setShowDetail(true);
        setDetailLoader(true);
        setDetail(null);
        GetFailedPriceValidationDetail({ id }).then(data => {
            setDetail(data?.data?.response || null);
            setDetailLoader(false);
        }).catch(error => {
            setDetailLoader(false);
            console.log("error ====> ", error);
        })
    };

    const deleteFunction = (id) => {
        Swal.fire({
            customClass: 'swal-wide',
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#403fad',
            cancelButtonColor: '#f1388b',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                DeleteFailedPriceValidation({ id }).then(data => {
                    if (data.status === 200) {
                        Swal.fire({
                            customClass: 'swal-wide',
                            icon: 'success',
                            title: 'Record deleted successfully',
                            showConfirmButton: false,
                            timer: 2000,
                            toast: true,
                            position: 'top-right',
                        })
                        setStatsUpdate(!statsUpdate);
                    }
                }).catch(error => {
                    console.log("deleteError ====> ", error);
                })
            }
        })
    };

    // Selection lives per page: paging or refiltering replaces the rows on screen,
    // so keeping ticks from a page you can no longer see would delete blind.
    const toggleRow = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const pageIds = list.map(row => row.id);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

    const toggleAllOnPage = () => {
        setSelectedIds(allOnPageSelected ? [] : pageIds);
    };

    // No delete-by-ids endpoint, so the picked rows go one call each. Bounded by the
    // page size, and the count reported back is what actually succeeded.
    const deleteSelectedFunction = () => {
        if (!selectedIds.length) return;
        Swal.fire({
            customClass: 'swal-wide',
            title: `Delete ${selectedIds.length} record(s)?`,
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#403fad',
            cancelButtonColor: '#f1388b',
            confirmButtonText: 'Yes, delete them!'
        }).then((result) => {
            if (!result.isConfirmed) return;
            setBulkBusy(true);
            Promise.all(selectedIds.map(id =>
                DeleteFailedPriceValidation({ id }).then(() => true).catch(() => false)
            )).then(results => {
                const deleted = results.filter(Boolean).length;
                const failed = results.length - deleted;
                setBulkBusy(false);
                setSelectedIds([]);
                Swal.fire({
                    customClass: 'swal-wide',
                    icon: failed ? 'warning' : 'success',
                    title: failed ? `${deleted} deleted, ${failed} failed` : `${deleted} record(s) deleted`,
                    showConfirmButton: false,
                    timer: 2500,
                    toast: true,
                    position: 'top-right',
                })
                setStatsUpdate(!statsUpdate);
            })
        })
    };

    // Everything the filters describe, not just this page. The API refuses an
    // unfiltered wipe and answers with the count, which becomes the confirmation.
    const deleteAllFunction = () => {
        const filters = buildFilters();
        if (!Object.keys(filters).length) {
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'info',
                title: 'Apply at least one filter first',
                text: 'Deleting every row at once is deliberately not allowed.',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                position: 'top-right',
            })
            return;
        }

        DeleteManyFailedPriceValidations(filters).then(() => {
            setSelectedIds([]);
            setStatsUpdate(!statsUpdate);
        }).catch(error => {
            const message = error?.response?.data?.errors?.message || error?.message || 'Unable to delete';
            Swal.fire({
                customClass: 'swal-wide',
                title: 'Delete all matching rows?',
                text: message,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#403fad',
                cancelButtonColor: '#f1388b',
                confirmButtonText: 'Yes, delete them!'
            }).then((result) => {
                if (!result.isConfirmed) return;
                DeleteManyFailedPriceValidations({ ...filters, confirm: 'DELETE' }).then(data => {
                    Swal.fire({
                        customClass: 'swal-wide',
                        icon: 'success',
                        title: `${data?.data?.response?.deleted ?? 0} record(s) deleted`,
                        showConfirmButton: false,
                        timer: 2000,
                        toast: true,
                        position: 'top-right',
                    })
                    setSelectedIds([]);
                    setStatsUpdate(!statsUpdate);
                }).catch(err => console.log("deleteManyError ====> ", err))
            })
        })
    };

    // Rows store ids only; names come from that company's cached tenant lists.
    const companyLabel = (row) => {
        const company = companies.find(item => String(item?.company_id) === String(row?.company_register_id));
        return company?.name || row?.company_register_id || row?.company_id || 'N/A';
    };

    const brandLabel = (row) => {
        const lists = tenantLists[adminCompanyIdFor(row?.company_register_id)];
        const brand = lists?.brands?.find(item => String(item?.id) === String(row?.brand_id));
        return brand?.name || (row?.brand_id ?? 'N/A');
    };

    const outletLabel = (row) => {
        const lists = tenantLists[adminCompanyIdFor(row?.company_register_id)];
        const outlet = lists?.outlets?.find(item => String(item?.id) === String(row?.outlet_id));
        if (!outlet) return row?.outlet_id ?? 'N/A';
        return `${outlet.outlet_name}${outlet.short_code ? ` (${outlet.short_code})` : ''}`;
    };

    const readableValidator = (value) => (value === 'new_validator' ? 'New validator' : value === 'old_validator' ? 'Old validator' : 'N/A');

    return (
        <Layout>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card overflow-hidden">
                    <div className="card-body">

                        {/* Old vs new validator at a glance — the reason this table exists. */}
                        <div className='row mb-4'>
                            <div className='col-md-3 mb-3'>
                                <div className='card custom-card mb-0 h-100' style={{ borderLeft: '4px solid #6c757d' }}>
                                    <div className='card-body p-3'>
                                        <p className='text-muted mb-1 tx-12'>Total failures</p>
                                        <h3 className='mb-0'>{dataLength || 0}</h3>
                                        <small className='text-muted'>matching current filters</small>
                                    </div>
                                </div>
                            </div>
                            {stats.map((row, i) => (
                                <div className='col-md-3 mb-3' key={i}>
                                    <div className='card custom-card mb-0 h-100' style={{ borderLeft: `4px solid ${row?.validator_type === 'new_validator' ? '#2f9bff' : '#f7b731'}` }}>
                                        <div className='card-body p-3'>
                                            <p className='text-muted mb-1 tx-12'>
                                                {readableValidator(row?.validator_type)} · {capitalizeAllFirstLetters(row?.operation || '')}
                                            </p>
                                            <h3 className='mb-0'>{row?.count ?? 0}</h3>
                                            <small className='text-muted'>
                                                Last: {row?.last_failed_at ? formateDateWithTime(row.last_failed_at) : 'N/A'}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className='d-flex justify-content-between align-items-start flex-wrap mb-3' style={{ gap: '12px' }}>
                            <div>
                                <h6 className="main-content-label mb-1">Failed Price Validations</h6>
                                <p className='text-muted mb-0 tx-12'>
                                    Orders the price validators rejected. Rows are written by the order pipeline — review, compare, then clear.
                                </p>
                            </div>
                            <div className='d-flex align-items-center flex-wrap' style={{ gap: '8px' }}>
                                <button type="reset" onClick={resetFilter} className="btn btn-warning" disabled={!activeFilterCount}>
                                    Reset Filter{activeFilterCount ? ` (${activeFilterCount})` : ''}
                                </button>
                                <button
                                    className="btn ripple btn-danger"
                                    onClick={deleteSelectedFunction}
                                    disabled={!selectedIds.length || bulkBusy}
                                >
                                    {bulkBusy ? 'Deleting…' : `Delete Selected${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
                                </button>
                                <button
                                    className="btn ripple btn-danger"
                                    onClick={deleteAllFunction}
                                    disabled={!dataLength}
                                    title='Deletes every row matching the current filters'
                                >
                                    Delete All Matching
                                </button>
                            </div>
                        </div>

                        {/* Filters get their own panel: seven of them in the heading row left
                            no room for the title and pushed the buttons onto a second line. */}
                        <div className='p-3 mb-4' style={{ background: '#f7f7fa', borderRadius: '6px' }}>
                            <div className='row'>
                                <div className='col-lg-3 col-md-6 mb-2'>
                                    <label className='tx-12 text-muted mb-1'>Search</label>
                                    <input
                                        type="search"
                                        className="form-control"
                                        value={search}
                                        placeholder="Error message contains…"
                                        onChange={(e) => { setPage(1); setSearch(e.target.value) }}
                                    />
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2'>
                                    <label className='tx-12 text-muted mb-1'>Company</label>
                                    <select className="form-control" value={companyId} onChange={(e) => handleCompanyChange(e.target.value)}>
                                        <option value="">All companies</option>
                                        {companies.map((row) => (
                                            <option key={row.id} value={row.id}>{row?.name || `#${row.id}`}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2'>
                                    <label className='tx-12 text-muted mb-1'>Brand</label>
                                    <select className="form-control" value={brandId} disabled={!companyId} onChange={(e) => { setPage(1); setBrandId(e.target.value); setOutletId('') }}>
                                        <option value="">{companyId ? 'All brands' : 'Select a company first'}</option>
                                        {brands.map((row) => (
                                            <option key={row.id} value={row.id}>{row?.name || `#${row.id}`}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2'>
                                    <label className='tx-12 text-muted mb-1'>Outlet</label>
                                    <select className="form-control" value={outletId} disabled={!companyId} onChange={(e) => { setPage(1); setOutletId(e.target.value) }}>
                                        <option value="">{companyId ? 'All outlets' : 'Select a company first'}</option>
                                        {outletOptions.map((row) => (
                                            <option key={row.id} value={row.id}>
                                                {row?.outlet_name || `#${row.id}`}{row?.short_code ? ` (${row.short_code})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2 mb-lg-0'>
                                    <label className='tx-12 text-muted mb-1'>Validator</label>
                                    <select className="form-control" value={validatorType} onChange={(e) => { setPage(1); setValidatorType(e.target.value) }}>
                                        {VALIDATOR_TYPES.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2 mb-lg-0'>
                                    <label className='tx-12 text-muted mb-1'>Operation</label>
                                    <select className="form-control" value={operation} onChange={(e) => { setPage(1); setOperation(e.target.value) }}>
                                        {OPERATIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2 mb-lg-0'>
                                    <label className='tx-12 text-muted mb-1'>Business date</label>
                                    <CustomRangepicker GetDateRange={(e) => { setPage(1); setBusinessDateRange(e) }} resetdate={resetdate} />
                                </div>
                                <div className='col-lg-3 col-md-6 mb-2 mb-lg-0'>
                                    <label className='tx-12 text-muted mb-1'>Created date</label>
                                    <CustomRangepicker GetDateRange={(e) => { setPage(1); setDateRange(e) }} resetdate={resetdate} />
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table id="example-input" className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                                <thead>
                                    <tr>
                                        <th width="3%">
                                            <input
                                                type="checkbox"
                                                className='cp'
                                                checked={allOnPageSelected}
                                                onChange={toggleAllOnPage}
                                                disabled={!list.length}
                                                title='Select all on this page'
                                            />
                                        </th>
                                        <th width="5%">Id</th>
                                        <th width="10%">Validator</th>
                                        <th width="8%">Operation</th>
                                        <th width="27%">Error Message</th>
                                        <th width="12%">Company</th>
                                        <th width="8%">Brand</th>
                                        <th width="8%">Outlet</th>
                                        <th width="9%">Business Date</th>
                                        <th width="11%">Created At</th>
                                        <th className='action_head' width="8%">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ?
                                        <tr>
                                            <td colSpan={11}><Loader /></td>
                                        </tr> : <>
                                            {list.length ? list.map((row, i) => (
                                                <tr key={i} className={selectedIds.includes(row.id) ? 'bg-light' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className='cp'
                                                            checked={selectedIds.includes(row.id)}
                                                            onChange={() => toggleRow(row.id)}
                                                        />
                                                    </td>
                                                    <td>#{row?.id}</td>
                                                    <td>
                                                        <span className={"badge " + (row?.validator_type === 'new_validator' ? 'bg-primary' : 'bg-warning')}>
                                                            {readableValidator(row?.validator_type)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className='badge bg-secondary'>
                                                            {row?.operation ? capitalizeAllFirstLetters(row.operation) : 'N/A'}
                                                        </span>
                                                    </td>
                                                    {/* The table is text-nowrap, so the message needs its own wrapping to
                                                        show in full instead of running off the row. */}
                                                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: '260px' }}>
                                                        {row?.error_message || 'N/A'}
                                                    </td>
                                                    <td>{companyLabel(row)}</td>
                                                    <td>{brandLabel(row)}</td>
                                                    <td>{outletLabel(row)}</td>
                                                    <td>{row?.business_date ? formateDate(row.business_date) : 'N/A'}</td>
                                                    <td>{row?.created_at ? formateDateWithTime(row.created_at) : 'N/A'}</td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <button className="btn ripple btn-main-primary signbtn" title='View payload' onClick={() => { viewFunction(row.id) }}>View</button>
                                                            <button className="ms-2 btn ripple btn-danger" title='Delete row' onClick={() => { deleteFunction(row.id) }}>Delete</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan={11} className="text-center py-5">
                                                        <i className='ti-check-box' style={{ fontSize: '28px', color: '#9aa0a6' }}></i>
                                                        <p className='mt-2 mb-0'>No failed price validations</p>
                                                        <small className='text-muted'>
                                                            {activeFilterCount ? 'Nothing matches these filters — try widening them.' : 'Both validators agreed on every order so far.'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            )}
                                        </>}
                                </tbody>
                            </table>
                        </div>

                        <div className='d-flex justify-content-between align-items-center flex-wrap mt-3' style={{ gap: '12px' }}>
                            <div id="example1_info" role="status" aria-live="polite"><b>Total Records : {dataLength ? dataLength : "0"}</b></div>
                            {dataLength && dataLength > 0 ?
                                <CustomPagination datalength={dataLength} itemperpage={itemPerPage} setPage={setPage} currentPage={page} pageRoute={[{ name: "failed-price-validation", path: "/admin/failed-price-validations/list" }]} /> : ""}
                        </div>
                    </div>
                </div>
            </div>

            {/* The payload is the replayable order body, so it is kept out of the
                list response and fetched only when a row is opened. */}
            <Modal size="lg" show={showDetail} onHide={() => setShowDetail(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Failed Price Validation {detail?.id ? `#${detail.id}` : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailLoader ? <Loader /> : detail ?
                        <>
                            <div className='row mb-3'>
                                <div className='col-md-6 mb-2'><b>Validator: </b>{readableValidator(detail?.validator_type)}</div>
                                <div className='col-md-6 mb-2'><b>Operation: </b>{detail?.operation ? capitalizeAllFirstLetters(detail.operation) : 'N/A'}</div>
                                <div className='col-md-6 mb-2'><b>Company: </b>{companyLabel(detail)}</div>
                                <div className='col-md-6 mb-2'><b>Brand / Outlet: </b>{brandLabel(detail)} / {outletLabel(detail)}</div>
                                <div className='col-md-6 mb-2'><b>Business Date: </b>{detail?.business_date ? formateDate(detail.business_date) : 'N/A'}</div>
                                <div className='col-md-6 mb-2'><b>Created At: </b>{detail?.created_at ? formateDate(detail.created_at) : 'N/A'}</div>
                                <div className='col-md-12 mb-2'><b>Error: </b>{detail?.error_message || 'N/A'}</div>
                            </div>
                            <b>Payload</b>
                            <pre className='mt-2 p-3' style={{ background: '#f7f7fa', maxHeight: '360px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                {detail?.payload ? JSON.stringify(detail.payload, null, 2) : 'N/A'}
                            </pre>
                        </>
                        : <p className='text-center mb-0'>No details found.</p>}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-outline-primary" onClick={() => setShowDetail(false)}>Close</button>
                </Modal.Footer>
            </Modal>
        </Layout>
    );
}

export default FailedPriceValidationsList
